import { Nullable } from '../types/general';

import {
    SESSION_EXPIRED_REDIRECT_LINK,
    WS_KEY_REGISTRATION_ATTEMPTS,
    WS_KEY_REMEMBER_KEYS,
} from '../lib/constants';
import { redirect } from '../lib/navigation';
import { getLocalStorageItem, removeLocalStorageItem } from '../lib/webStorage';
import io from './io';

export interface RegistrationMetaData {
    referrer?: string;
    /**
     * Link after finishing the fist registration step
     */
    redirect?: string;
    /**
     * From what landing (edi, kasa, edo)
     */
    source?: string;
    token?: string;
    /**
     * Enable trial after success sign-up
     */
    isActiveProTrial?: boolean;
    invite_email?: string | null;
}

export interface GoogleAuthOptions extends RegistrationMetaData {
    token: string;
}

export interface MicrosoftAuthOptions extends RegistrationMetaData {
    access_token: string;
    id_token: string;
}

export interface AppleAuthUserPayload {
    email: string;
    name: {
        firstName: string;
        lastName: string;
    };
}

export interface AppleAuthOptions extends RegistrationMetaData {
    code: string;
    user?: AppleAuthUserPayload;
}

async function colbertIdentify() {
    return await io.getAsJson('/internal-api/colbert/identify');
}

interface LoginPayload {
    email: string;
    password: string;
    remember: boolean;
}

async function login(payload: LoginPayload) {
    return await io.post('/auth-api/login', payload, true, {});
}

export interface RegistrationOptions extends RegistrationMetaData {
    email: string;
    password: string;
    name?: string;
    phone?: string;
    isActiveProTrial?: boolean;
}

interface CommonRegistrationPayload
    extends Omit<RegistrationOptions, 'isActiveProTrial'> {
    trial_auto_enable: boolean;
}

async function register({
    isActiveProTrial = false,
    token,
    referrer,
    ...common
}: RegistrationOptions) {
    const payload: CommonRegistrationPayload = {
        ...common,
        trial_auto_enable: isActiveProTrial,
    };

    if (token) {
        payload.token = token;

        return await io.post('/auth-api/registration/token', payload);
    }

    payload.referrer = referrer;

    return await io.post('/auth-api/registration', payload);
}

const LOGOUT_REDIRECT_URL = config.LANDING_URL || '/auth';

const successLogout = (redirectUrl: string) => {
    sessionStorage.clear();
    removeLocalStorageItem(WS_KEY_REMEMBER_KEYS);
    broadcastAuthChannel.logOut();
    redirect(redirectUrl);
};

async function logout({ redirectUrl = LOGOUT_REDIRECT_URL } = {}) {
    const resp = await io.post('/auth-api/logout');
    if (resp.ok) {
        successLogout(redirectUrl);
    }
}

async function logoutAllSessions({ redirectUrl = LOGOUT_REDIRECT_URL } = {}) {
    const resp = await io.post('/auth-api/logout-all-sessions', {});
    if (resp.ok) {
        successLogout(redirectUrl);
    }
}

export interface InviteUserOptionsPayload {
    email: string;
    edrpou: string;
    permissions?: {
        can_view_document: boolean;
        can_comment_document: boolean;
        can_upload_document: boolean;
        can_download_document: boolean;
        can_print_document: boolean;
        can_delete_document: boolean;
        can_sign_and_reject_document: boolean;
        can_invite_coworkers: boolean;
        can_edit_company: boolean;
        can_edit_roles: boolean;
        can_edit_document_automation: boolean;
        can_edit_document_fields: boolean;
        can_edit_templates: boolean;
        can_create_tags: boolean;
        is_admin: boolean;
    };
    isPermissionChecked?: boolean;
    position?: Nullable<string>;
}

async function inviteUser(payload: InviteUserOptionsPayload) {
    return await io.post('/internal-api/invite', payload);
}

interface RemindPassOptions {
    email: string;
}

async function remindPass(options: RemindPassOptions) {
    return await io.post('/internal-api/profile/password/remind', options);
}

interface RecoverPassOptions {
    password: string;
    token: string;
}

async function recoverPass(options: RecoverPassOptions) {
    const resp = await io.post(
        '/internal-api/profile/password/recover',
        options,
    );
    if (resp.ok) {
        redirect('/auth');
    }
}

interface UpdatePasswordOptions {
    /**
     * In case if password was auto-generated we can update with new one but only once
     */
    new_password: string;
    current_password?: string;
    isAutogeneratedPassword?: boolean;
}

async function updatePass(options: UpdatePasswordOptions) {
    await io.post('/internal-api/profile/password', options);
}

async function activateRole(roleId: string) {
    return await io.post(`/internal-api/roles/${roleId}/activate`);
}

async function verify2FACode(code: string, trusted: boolean) {
    const response = await io.post(
        '/internal-api/2fa/verify',
        { code, trusted },
        true,
    );
    return response.next_url;
}

async function resend2FACode() {
    await io.post('/internal-api/2fa/resend');
}

async function sendRegistrationAttempts() {
    const attempts = JSON.parse(
        getLocalStorageItem(WS_KEY_REGISTRATION_ATTEMPTS),
    );
    await io.post('/auth-api/registration/info', { attempts });
}

interface ShortRegisterOptions {
    name?: string;
    phone?: string;
    password: string;
    token: string;
}

async function shortRegister(options: ShortRegisterOptions) {
    return await io.post('/auth-api/registration/short', options);
}

type AcceptOffersShortRegister = Pick<ShortRegisterOptions, 'token'>;

async function acceptOffersShortRegister({ token }: AcceptOffersShortRegister) {
    return await io.post('/auth-api/registration/short/offers', { token });
}

type GoogleAuthFlow = 'registration' | 'login';

export interface ThirdPartyAPIAuthResponse {
    next_url: string;
    is_2fa_enabled?: boolean;
    flow: GoogleAuthFlow;
    email: string;
}

export type APIGoogleAuthResponse = ThirdPartyAPIAuthResponse;
export type APIMicrosoftAuthResponse = ThirdPartyAPIAuthResponse;
export type APIAuthAuthResponse = ThirdPartyAPIAuthResponse;

export interface ThirdPartyAuthResponse {
    nextUrl: string;
    is2FAEnabled: boolean;
    flow: GoogleAuthFlow;
    email: string;
}

export type GoogleAuthResponse = ThirdPartyAuthResponse;
export type MicrosoftAuthResponse = ThirdPartyAuthResponse;
export type AppleAuthResponse = ThirdPartyAuthResponse;

async function googleAuth(
    options: GoogleAuthOptions,
): Promise<GoogleAuthResponse> {
    const response: APIGoogleAuthResponse = await io.post(
        '/auth-api/providers/google',
        options,
        true,
    );
    return {
        nextUrl: response.next_url,
        is2FAEnabled: Boolean(response.is_2fa_enabled),
        flow: response.flow,
        email: response.email,
    };
}

async function microsoftAuth(
    options: MicrosoftAuthOptions,
): Promise<MicrosoftAuthResponse> {
    const response: APIMicrosoftAuthResponse = await io.post(
        '/auth-api/providers/microsoft',
        options,
        true,
    );

    return {
        nextUrl: response.next_url,
        is2FAEnabled: Boolean(response.is_2fa_enabled),
        flow: response.flow,
        email: response.email,
    };
}

export const appleAuth = async (
    options: AppleAuthOptions,
): Promise<AppleAuthResponse> => {
    const response: APIAuthAuthResponse = await io.post(
        '/auth-api/providers/apple',
        options,
        true,
    );

    return {
        nextUrl: response.next_url,
        is2FAEnabled: Boolean(response.is_2fa_enabled),
        flow: response.flow,
        email: response.email,
    };
};

/**
 * This endpoint registers a phone number and sends an OTP to that number
 */
async function registerPhone(phone: string): Promise<void> {
    return await io.post('/internal-api/registration/phone', {
        phone,
    });
}

/**
 * This endpoint will put a new phone number and send OTP again.
 * It has a limit of sending no more than one message per minute
 */
async function updatePhone(phone: string): Promise<void> {
    return await io.patch('/internal-api/registration/phone', {
        phone,
    });
}

async function checkUserIsRegistered(email: string): Promise<void> {
    const formattedEmail = encodeURIComponent(email);
    return await io.get(
        `/internal-api/registration/check-email?email=${formattedEmail}`,
    );
}

async function changeRegistrationEmail(
    email: string,
    redirectLink: string,
): Promise<void> {
    return await io.patch('/auth-api/registration/change-email', {
        email,
        redirect: redirectLink,
    });
}

async function getHiddenPhone(): Promise<{ phone: string }> {
    return await io.getAsJson('/internal-api/2fa/hidden-phone');
}

interface BroadcastAuthMessageProps {
    action: 'logIn' | 'logOut' | 'sessionExpired';
    currentUserID: Nullable<string>;
}

export class BroadcastAuthChannel {
    private broadcastChannel = new BroadcastChannel('AUTH');
    private static instance: Nullable<BroadcastAuthChannel> = null;

    public currentUserID: Nullable<string> = null;

    constructor() {
        if (!BroadcastAuthChannel.instance) {
            BroadcastAuthChannel.instance = this;

            this.broadcastChannel.addEventListener(
                'message',
                (event: MessageEvent<BroadcastAuthMessageProps>) => {
                    const { data } = event;

                    if (
                        data.action === 'logIn' &&
                        data.currentUserID !== this.currentUserID
                    ) {
                        window.location.reload();
                        return;
                    }

                    if (data.action === 'logOut') {
                        window.location.reload();
                        return;
                    }

                    if (data.action === 'sessionExpired') {
                        window.location.assign(SESSION_EXPIRED_REDIRECT_LINK);
                        return;
                    }
                },
            );
        }

        return BroadcastAuthChannel.instance;
    }

    logIn(userID: string) {
        this.currentUserID = userID;
        this.broadcastChannel.postMessage({
            action: 'logIn',
            currentUserID: userID,
        });
    }

    logOut() {
        this.currentUserID = null;
        this.broadcastChannel.postMessage({
            action: 'logOut',
            currentUserID: null,
        });
    }

    sessionExpired() {
        this.currentUserID = null;
        this.broadcastChannel.postMessage({
            action: 'sessionExpired',
            currentUserID: null,
        });
    }
}

export const broadcastAuthChannel = new BroadcastAuthChannel();

export const logoutSession = async ({
    sessionId,
    userId = null, // передаємо id користувача, якщо потрібно завершити сесію конкретного користувача (параметр для адміна)
}: {
    sessionId: string;
    userId?: Nullable<string>;
}) =>
    await io.post('/auth-api/logout-session', {
        session_id: sessionId,
        user_id: userId,
    });

export const logoutSessions = async ({
    userId = null, // передаємо id користувача, якщо потрібно завершити сесії конкретного користувача (параметр для адміна)
    withMobile = false, // чи потрібно завершувати сесії на мобільних пристроях
    logoutCurrent = false, // чи потрібно завершувати поточну сесію
}: {
    userId: Nullable<string>;
    withMobile?: Nullable<boolean>;
    logoutCurrent?: boolean;
}) =>
    await io.post('/auth-api/logout-all-sessions', {
        with_mobile: withMobile,
        terminate_current_session: logoutCurrent,
        user_id: userId,
    });

export default {
    activateRole,
    registerPhone,
    updatePhone,
    colbertIdentify,
    inviteUser,
    login,
    logout,
    logoutAllSessions,
    recoverPass,
    register,
    remindPass,
    resend2FACode,
    updatePass,
    verify2FACode,
    sendRegistrationAttempts,
    shortRegister,
    acceptOffersShortRegister,
    googleAuth,
    microsoftAuth,
    checkUserIsRegistered,
    changeRegistrationEmail,
    getHiddenPhone,
    logoutSession,
};
